/**
 * Utility functions for measuring and logging performance metrics
 */

// Map to store start times for performance measurements
const performanceTimers = new Map<string, number>();

/**
 * Start timing an operation
 * @param operationName Unique name for the operation being timed
 */
export const startMeasuring = (operationName: string): void => {
  performanceTimers.set(operationName, performance.now());
  console.log(`⏱️ Starting measurement: ${operationName}`);
};

/**
 * End timing an operation and log the result
 * @param operationName The name used when starting the measurement
 * @param additionalInfo Optional additional context information
 * @returns The duration in milliseconds, or -1 if no start time was found
 */
export const endMeasuring = (
  operationName: string,
  additionalInfo?: Record<string, any>,
): number => {
  const startTime = performanceTimers.get(operationName);

  if (startTime === undefined) {
    console.warn(`⚠️ No start time found for operation: ${operationName}`);
    return -1;
  }

  const endTime = performance.now();
  const duration = endTime - startTime;

  console.log(
    `⏱️ ${operationName} completed in ${duration.toFixed(2)}ms`,
    additionalInfo ? additionalInfo : "",
  );

  performanceTimers.delete(operationName);
  return duration;
};

/**
 * Safely ends a measurement if it exists, or does nothing if it doesn't
 * @param operationName The name used when starting the measurement
 * @param additionalInfo Optional additional context information
 * @returns The duration in milliseconds, or -1 if no start time was found
 */
export const safelyEndMeasuring = (
  operationName: string,
  additionalInfo?: Record<string, any>,
): number => {
  if (performanceTimers.has(operationName)) {
    return endMeasuring(operationName, additionalInfo);
  }
  return -1;
};

/**
 * Wraps an async function with performance measurement
 * @param operationName Name for the operation
 * @param fn Function to measure
 * @param additionalInfo Optional additional context
 * @returns The result of the measured function
 */
export const measureAsync = async <T>(
  operationName: string,
  fn: () => Promise<T> | T,
  additionalInfo?: Record<string, any>,
): Promise<T> => {
  startMeasuring(operationName);
  try {
    const result = await fn();
    endMeasuring(operationName, additionalInfo);
    return result;
  } catch (error) {
    endMeasuring(operationName, { error, ...additionalInfo });
    throw error;
  }
};
